import React, { useEffect, useState, useMemo } from "react";
import PageCard from "features/shared/components/card/PageCard";
import FormGroup from "features/shared/forms/components/FormGroup";
import SelectInput from "features/shared/components/selectInput/SelectInput";
import OptionType from "features/shared/components/selectInput/OptionType";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import { useForm } from "features/shared/forms/hooks/useForm";
import { ets2AffidavitFormValidations } from "./ets2AffidavitFormValidation";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IRequirementCreateService from "features/requirements/services/IRequirementCreateService";
import { openRequirementResultDialog } from "features/requirements/helpers/requirementResultDialogHelper";
import BaseRequirementFormProps from "features/requirements/models/BaseRequirementFormProps";
import BasicInfoSubFormSection from "../basicInfoSubForm/BasicInfoSubFormSection";
import { useQuery } from "data/hooks/useQuery";
import { Enums_Enums } from "data/endpoints";
import { FlexiEnumType } from "features/shared/enums/flexiEnumType";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import DataQuery from "data/DataQuery";
import {
  Customer_Get,
  PlaceOfDelivery_ContractsDuration,
  Customer_Ets2EligibleContracts,
} from "data/endpoints";
import CustomerDTO from "features/customer/dtos/CustomerDTO";
import { CustomerType } from "features/customer/enums/customerType";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { getAddressSections } from "features/shared/helpers/AddressFormatter";
import ContractDropdownDTO from "features/contracts/dtos/ContractDropdownDTO";
import {
  getQueryParams,
  getPlaceOfDeliveryContractsDurationParams,
} from "features/shared/helpers/queryParams";
import ConditionalExecute from "data/models/ConditionalExecute";
import { isYearWithinContract } from "features/contracts/helpers/contractDateHelper";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import Alert from "features/shared/alerts/components/Alert";
import ContractSelectUnified from "features/contracts/components/ContractSelectUnified";
import { ContractStatus } from "features/contracts/enums/contractStatus";
import PostResponse from "data/PostResponse";
import InsertRequirementResultDTO from "features/requirements/dtos/InsertRequirementResultDTO";
import { RequirementResultDialogParams } from "features/requirements/models/RequirementResultDialogParams";
import { isSuccessStatus } from "data/helpers/responseHelper";
import IRepository from "data/IRepository";
import { Ets2_AffidavitSignUrl } from "data/endpoints";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import { trackPromise } from "react-promise-tracker";
import RadioBoxBoolSubForm from "features/shared/forms/components/radioBox/RadioBoxBoolSubForm";

interface Props extends BaseRequirementFormProps {}

const ETS2AffidavitFormSection = (props?: Props) => {
  //#region translations
  const tLabels = useTranslationWithPrefix(
    "requirements\\requirements",
    "ets2AffidavitFormSection"
  );
  //#endregion translations

  //#region DI
  const createService = useInjection<IRequirementCreateService>(
    IocTypes.RequirementCreateService
  );
  const signUrlRepository = useInjection<IRepository<ActionResultDTO<string>>>(
    IocTypes.Repository
  );
  //#endregion DI

  //#region Props
  const useOutsideOfRequirements = props?.useOutsideOfRequirements
    ? true
    : false;
  //#endregion Props

  //#region store
  const customer = useSelector<IAppState, CustomerState>(
    (s) => s.customer
  ).customer;
  //#endregion store

  //#region state
  const [contracts, setContracts] = useState<ContractDropdownDTO[]>([]);
  const [redirectTimeoutId, setRedirectTimeoutId] = useState<number | null>(
    null
  );
  //#endregion state

  //#region form hook
  const formHandler = useForm({
    initialValidations: ets2AffidavitFormValidations(customer.customerType!),
  });

  const {
    values,
    errors,
    bindFieldNew,
    bindUncontrolledField,
    bindReactSelectField,
    valideteForm,
    clearValidationErrors,
    clearFormValues,
    getFormValue,
    isFilledFormValues,
  } = {
    ...formHandler,
  };
  //#endregion form hook

  //#region side-effects
  // fetch years
  const yearsQuery: DataQuery = {
    url: PlaceOfDelivery_ContractsDuration,
    params: getPlaceOfDeliveryContractsDurationParams(
      customer.customerId,
      undefined,
      ["GasCustomer"],
      undefined,
      [ContractStatus.Effective],
      true
    ),
  };
  const { data: years, loadStatus: yearsStatus } = useQuery<number[]>(
    yearsQuery,
    [],
    [customer.customerId]
  );
  const isYearsAvailable: ConditionalExecute = {
    apply: true,
    // Load data after years are available
    condition: () => years && years.length > 0,
  };

  // fetch customer
  const customerQuery: DataQuery = {
    url: Customer_Get,
    routeParams: getRouteParams("customerId", customer.customerId!),
  };
  const { data: fullCustomer } = useQuery<CustomerDTO>(
    customerQuery,
    {},
    [years],
    { useTracker: true },
    isYearsAvailable
  );

  // fetch eligible contracts
  const params = getQueryParams("year", values["DESC_ETS2_ROK"] ?? "");
  const contractsQuery: DataQuery = {
    url: Customer_Ets2EligibleContracts,
    routeParams: getRouteParams("customerId", customer.customerId!),
    params: params,
  };

  const isEligibleContractsAvailable: ConditionalExecute = {
    apply: true,
    condition: () =>
      years && years.length > 0 && !!values["DESC_ETS2_ROK"],
  };

  const { data: fetchedContracts } = useQuery<ContractDropdownDTO[]>(
    contractsQuery,
    [],
    [years, values["DESC_ETS2_ROK"]],
    { useTracker: true },
    isEligibleContractsAvailable
  );

  // fetch FLEXI enums
  const enumsParams = getQueryParams("EnumsCode", [
    FlexiEnumType.Ets2ConsumptionType,
    FlexiEnumType.Ets2BusinessType,
    FlexiEnumType.SubjectRelationType,
  ]);
  const enumsQuery: DataQuery = { url: Enums_Enums, params: enumsParams };
  const { data: flexiEnums } = useQuery<Record<string, UserEnumDTO[]>>(
    enumsQuery,
    {},
    [years],
    { useTracker: true },
    isYearsAvailable
  );

  const consumptionEnum = flexiEnums?.[FlexiEnumType.Ets2ConsumptionType] ?? [];
  const businessEnum = flexiEnums?.[FlexiEnumType.Ets2BusinessType] ?? [];
  const subjectRelationEnum =
    flexiEnums?.[FlexiEnumType.SubjectRelationType] ?? [];

  // filter contracts by selected year
  useEffect(() => {
    if (Array.isArray(fetchedContracts)) {
      setContracts(fetchedContracts);
    } else {
      setContracts([]);
    }

    clearFormValues(["DESC_ETS2_OPM"]);
  }, [fetchedContracts, values["DESC_ETS2_ROK"]]);

  // clear OPM values when consumption type changes
  useEffect(() => {
    clearFormValues(["DESC_ETS2_OPM"]);
    clearValidationErrors(["DESC_ETS2_OPM"]);
  }, [values["DESC_ETS2_TYP_SPOTREBY"]]);

  // clear redirect timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutId) {
        clearTimeout(redirectTimeoutId);
      }
    };
  }, [redirectTimeoutId]);
  //#endregion side-effects

  //#region memoized values
  const lowConsumptionSelected = useMemo(() => {
    const descVal: string = getFormValue("DESC_ETS2_TYP_SPOTREBY");
    if (!descVal) return false;

    const forestryEnum = consumptionEnum.find((x) => x.id === descVal);

    return forestryEnum !== undefined && forestryEnum.code === "NizkaSpotreba";
  }, [consumptionEnum, getFormValue("DESC_ETS2_TYP_SPOTREBY")]);
  //#endregion memoized values

  //#region handlers
  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      let valuesCopy = { ...values };

      let opmValue = values["DESC_ETS2_OPM"];
      valuesCopy["DESC_ETS2_OPM"] = Array.isArray(opmValue)
        ? opmValue.join(";")
        : opmValue;

      const result = await createService.createRequirement(
        "ETS2_CESTNEPROHLASENI",
        valuesCopy
      );

      let redirectUrl = "";

      if (isSuccessStatus(result) && result.data?.createdEntityId) {
        const signUrlResponse = await trackPromise(
          fetchSignatureUrl(result.data.createdEntityId)
        );

        if (
          !signUrlResponse.result ||
          signUrlResponse.validationMessages.length > 0
        ) {
          const errorResponse: PostResponse<InsertRequirementResultDTO> = {
            status: 500,
            data: { validationMessages: signUrlResponse.validationMessages },
          };
          openResultDialogHandler(errorResponse, redirectUrl);
        } else {
          redirectUrl = signUrlResponse.result;
          openResultDialogHandler(result, redirectUrl);
          scheduleRedirect(redirectUrl);
        }
      } else {
        openResultDialogHandler(result, redirectUrl);
      }
    }
  };

  const openResultDialogHandler = (
    result: PostResponse<InsertRequirementResultDTO>,
    redirectUrl: string
  ) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: tLabels("successMessage"),
      errorMessage: tLabels("errorMessage"),
      actionBtn: { title: tLabels("redirectToSignature"), route: redirectUrl, isExternal: true },
      transitonBtn: null,
    };

    openRequirementResultDialog(
      useOutsideOfRequirements,
      params,
      tLabels("cardTitle")
    );
  };
  //#endregion handlers

  //#region helpers
  const scheduleRedirect = (urlFromApi: string) => {
    const id = window.setTimeout(() => {
      window.location.assign(urlFromApi);
    }, 5000);
    setRedirectTimeoutId(id);
  };

  const fetchSignatureUrl = async (
    createdEntityId: number
  ): Promise<ActionResultDTO<string>> => {
    try {
      return await signUrlRepository.getOne({
        url: Ets2_AffidavitSignUrl,
        routeParams: getRouteParams(
          "ets2AffidavitId",
          createdEntityId.toString()
        ),
      });
    } catch (error) {
      return { result: "", validationMessages: [] };
    }
  };
  //#endregion helpers

  //#region render
  return (
    <NoContentWrapper
      data={years}
      loadStatus={yearsStatus}
      noContentAsAlert
      noContentInfo={tLabels("noOpmYearsFound")}
    >
      <form onSubmit={submitHandler}>
        <div className="row no-gutters mx-n2 order-1">
          <div className="col-xl-8 px-2">
            <PageCard
              title={tLabels("cardTitle")}
              bodyClassName="pt-4 px-4"
              expandToHeight
            >
              <div className="row no-gutters mx-n2">
                <div className="col-sm-6 px-2">
                  <SelectInput
                    {...bindFieldNew("DESC_ETS2_TYP_SPOTREBY")}
                    options={consumptionEnum}
                    optionName="name"
                    optionValue="id"
                    label={tLabels("consumptionType")}
                    defaultValueLabel={tLabels("selectConsumptionType")}
                    validationError={errors["DESC_ETS2_TYP_SPOTREBY"]}
                  />
                </div>
                <div className="col-sm-6 px-2">
                  <SelectInput
                    {...bindFieldNew("DESC_ETS2_ROK")}
                    options={(years ?? []).map<OptionType>((y) => ({
                      value: String(y),
                      name: String(y),
                    }))}
                    label={tLabels("year")}
                    defaultValueLabel={tLabels("setYear")}
                    validationError={errors["DESC_ETS2_ROK"]}
                  />
                </div>
                <div className="col-sm-6 px-2">
                  <ContractSelectUnified
                    {...bindReactSelectField(
                      "DESC_ETS2_OPM",
                      "placeOfDeliveryId",
                      contracts,
                      !lowConsumptionSelected
                    )}
                    contracts={contracts}
                    label={tLabels("opmIds")}
                    placeholder={tLabels("setOpmIds")}
                    noOptionsMessage={
                      !isFilledFormValues(["DESC_ETS2_ROK"])
                        ? tLabels("selectAffidavitYear")
                        : undefined
                    }
                    errors={errors}
                    type="PlaceOfDelivery"
                  />
                </div>
                <div className="col-sm-6 px-2">
                  <SelectInput
                    {...bindFieldNew("DESC_ETS2_TYP_PODNIKANI")}
                    options={businessEnum}
                    optionName="name"
                    optionValue="id"
                    label={tLabels("businessType")}
                    defaultValueLabel={tLabels("selectBusinessType")}
                    validationError={errors["DESC_ETS2_TYP_PODNIKANI"]}
                  />
                </div>
                <RenderWrapper isVisible={() => lowConsumptionSelected}>
                  <div className="col-12 px-2 pt-2">
                    <Alert
                      alertObj={{
                        alertType: "Info",
                        message: `${tLabels("consumptionTypeInfo1")} ${tLabels(
                          "consumptionTypeInfo2"
                        )}`,
                      }}
                    />
                  </div>
                </RenderWrapper>
              </div>
            </PageCard>
          </div>
          <div className="col-xl-4 px-2 order-2 order-xl-1">
            <PageCard
              title={tLabels("customerInfoCardTitle")}
              bodyClassName="pt-4 px-4"
            >
              <div className="row no-gutters">
                <div className="col-sm-6 col-xl-12 px-2">
                  <FormGroup
                    labelText={tLabels("customerName")}
                    tooltip={{
                      id: "ets2CustomerNameTip",
                      content: tLabels("customerInfoTooltip"),
                    }}
                  >
                    <input
                      className="form-control"
                      disabled
                      value={fullCustomer?.name ?? ""}
                    />
                  </FormGroup>
                </div>
                <div className="col-sm-6 col-xl-12 px-2">
                  <FormGroup
                    labelText={
                      fullCustomer?.customerType === CustomerType.Company
                        ? tLabels("companyAddress")
                        : tLabels("personAddress")
                    }
                    tooltip={{
                      id: "ets2CustomerAddrTip",
                      content: tLabels("customerInfoTooltip"),
                    }}
                  >
                    <input
                      className="form-control"
                      disabled
                      value={
                        fullCustomer?.address
                          ? (() => {
                              const a = getAddressSections(
                                fullCustomer.address
                              );
                              return `${a.streetSection}, ${a.citySection}`;
                            })()
                          : ""
                      }
                    />
                  </FormGroup>
                </div>
                <RenderWrapper
                  isVisible={() =>
                    fullCustomer?.customerType === CustomerType.Company
                  }
                >
                  <div className="col-sm-6 col-xl-12 px-2">
                    <FormGroup
                      labelText={tLabels("companyRegistrationNumber")}
                      tooltip={{
                        id: "ets2CustomerCinTip",
                        content: tLabels("customerInfoTooltip"),
                      }}
                    >
                      <input
                        className="form-control"
                        disabled
                        value={
                          fullCustomer?.company?.companyRegistrationNumber ?? ""
                        }
                      />
                    </FormGroup>
                  </div>
                </RenderWrapper>
              </div>
            </PageCard>
          </div>
          <RenderWrapper
            isVisible={() =>
              fullCustomer?.customerType === CustomerType.Company
            }
          >
            <div className="col-12 px-2 order-1">
              <PageCard
                title={tLabels("authorizedPersonCardTitle")}
                bodyClassName="pt-4 px-4"
              >
                <div className="row no-gutters mx-n2">
                  <div className="col-sm-6 px-2">
                    <FormGroup
                      labelText={tLabels("personName")}
                      validationError={errors["DESC_ETS2_POVERENA_OSOBA_JMENO"]}
                    >
                      <input
                        className="form-control"
                        placeholder={tLabels("setPersonName")}
                        {...bindFieldNew("DESC_ETS2_POVERENA_OSOBA_JMENO")}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-sm-6 px-2">
                    <FormGroup
                      labelText={tLabels("personSurname")}
                      validationError={
                        errors["DESC_ETS2_POVERENA_OSOBA_PRIJMENI"]
                      }
                    >
                      <input
                        className="form-control"
                        placeholder={tLabels("setPersonSurname")}
                        {...bindFieldNew("DESC_ETS2_POVERENA_OSOBA_PRIJMENI")}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-sm-6 px-2">
                    <FormGroup
                      labelText={tLabels("personPosition")}
                      validationError={
                        errors["DESC_ETS2_POVERENA_OSOBA_POZICE"]
                      }
                    >
                      <input
                        className="form-control"
                        placeholder={tLabels("setPersonPosition")}
                        {...bindFieldNew("DESC_ETS2_POVERENA_OSOBA_POZICE")}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-sm-6 px-2">
                    <RadioBoxBoolSubForm
                      name={"DESC_ETS2_POVERENA_OSOBA_OPRAVNEN_JEDNAT"}
                      label={tLabels("authorizationToAct")}
                      formHandler={formHandler}
                      tooltip={{
                        id: "ets2AuthorizationToActTip",
                        content: tLabels("authorizationToActInfoTooltip"),
                      }}
                    />
                  </div>
                </div>
              </PageCard>
            </div>
          </RenderWrapper>
        </div>

        <RenderWrapper isVisible={() => !useOutsideOfRequirements}>
          <BasicInfoSubFormSection
            bindField={bindFieldNew}
            bindUncontrolledField={bindUncontrolledField}
            errors={errors}
            setValidationRule={() => {}}
          />
        </RenderWrapper>

        <button type="submit" className="btn btn-primary px-3 py-2">
          {tLabels("sendRequirement")}
        </button>
      </form>
    </NoContentWrapper>
  );
  //#endregion render
};

export default ETS2AffidavitFormSection;
